import { sessionHeaders } from 'src/api/teslaTrip'

// ----------------------------------------------------------------------
// The console's Apple round trip, as the backend holds it.
//
// Google's trip (`googleTrip`) with one step more, which the console never
// sees: Apple posts its answer from its own page to this origin's
// `/backoffice/auth/apple/callback`, where the API keeps it for the trip this
// browser holds and sends the browser on to `/login/apple/callback` with the
// state. From there the trip is finished here, on the console's own origin
// and never on `VITE_BACKOFFICE_URL`, so the HttpOnly cookie that binds it to
// this browser is first-party and sent.
// ----------------------------------------------------------------------

export type AppleTripPurpose = 'sign_in' | 'link'

export type AppleTripOutcome =
  | {
      purpose: 'sign_in'
      token: string
      invitation: 'accepted' | 'unavailable' | null
    }
  /** The account's authenticator code is owed before Apple is linked. */
  | { purpose: 'sign_in'; secondFactorTicket: string }
  /**
   * No console account uses it, and none was made: the person is asked first
   * (`newAccountConfirm`). The address is the one Apple proved.
   */
  | { purpose: 'sign_in'; newAccountTicket: string; email: string | null }
  | { purpose: 'link'; linkedEmail: string | null }

/** A refusal from the backend, with its code and what it said. */
export class AppleTripError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'AppleTripError'
  }
}

const UNREACHABLE =
  'Apple sign in could not start. Check your connection and try again.'

interface GraphQLAnswer<T> {
  data?: T | null
  errors?: { message?: string; extensions?: { code?: string } }[]
}

const post = async <T>(
  query: string,
  variables: object,
  withSession: boolean,
): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${window.location.origin}/backoffice/graphql`, {
      method: 'POST',
      // The default, stated: the cookie is this origin's and goes only here.
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(withSession ? await sessionHeaders() : {}),
      },
      body: JSON.stringify({ query, variables }),
    })
  } catch {
    throw new AppleTripError('UNREACHABLE', UNREACHABLE)
  }

  const answer = (await response.json().catch(() => ({}))) as GraphQLAnswer<T>
  const [error] = answer.errors ?? []
  if (error || !answer.data) {
    throw new AppleTripError(
      error?.extensions?.code ?? 'UNKNOWN',
      error?.message ?? UNREACHABLE,
    )
  }

  return answer.data
}

const START = `mutation AuthAppleStart($input: MutationAuthAppleStartInput!) {
  authAppleStart(input: $input) { url }
}`

const FINISH = `mutation AuthAppleFinish($input: MutationAuthAppleFinishInput!) {
  authAppleFinish(input: $input) {
    purpose
    token
    invitation
    secondFactorTicket
    newAccountTicket
    newAccountEmail
    linkedEmail
  }
}`

/** Begins a trip and answers with where to send the browser. */
export const appleTripStart = async ({
  purpose,
  invitationToken,
}: {
  purpose: AppleTripPurpose
  invitationToken?: string | null
}): Promise<string> => {
  // A link acts for the person signed in; a sign-in needs nobody.
  const { authAppleStart } = await post<{ authAppleStart: { url: string } }>(
    START,
    { input: { purpose, invitationToken: invitationToken ?? null } },
    purpose === 'link',
  )
  return authAppleStart.url
}

/** Finishes the trip Apple's answer was kept for, in this browser. */
export const appleTripFinish = async (
  state: string,
): Promise<AppleTripOutcome> => {
  const { authAppleFinish: outcome } = await post<{
    authAppleFinish: {
      purpose: AppleTripPurpose
      token: string | null
      invitation: 'accepted' | 'unavailable' | null
      secondFactorTicket: string | null
      newAccountTicket: string | null
      newAccountEmail: string | null
      linkedEmail: string | null
    }
  }>(FINISH, { input: { state } }, true)

  if (outcome.purpose === 'link') {
    return { purpose: 'link', linkedEmail: outcome.linkedEmail }
  }
  if (outcome.secondFactorTicket) {
    return {
      purpose: 'sign_in',
      secondFactorTicket: outcome.secondFactorTicket,
    }
  }
  if (outcome.newAccountTicket) {
    return {
      purpose: 'sign_in',
      newAccountTicket: outcome.newAccountTicket,
      email: outcome.newAccountEmail,
    }
  }
  if (!outcome.token) {
    throw new AppleTripError('UNKNOWN', 'Apple sign in failed. Try again.')
  }
  return {
    purpose: 'sign_in',
    token: outcome.token,
    invitation: outcome.invitation,
  }
}
